import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchPodProducts, PodProduct, POD_DIVISION_ID, POD_DIVISION_NAME } from '../../pod';
import { SearchProduct } from '../product-search/shared/types';

function mapToSearchProduct(product: PodProduct): SearchProduct {
  return {
    id: product.ext_product_id,
    name: product.product_name,
    supplierCode: product.product_supplier_code,
    divisionId: POD_DIVISION_ID,
    divisionName: POD_DIVISION_NAME,
    currency: product.currency || 'USD',
    minPrice: product.min_price ?? undefined,
    maxPrice: product.max_price ?? undefined,
    imageUrl: product.product_image || undefined,
    _raw: {
      ...product,
      division_id: POD_DIVISION_ID,
      division_name: POD_DIVISION_NAME,
      product_img: product.product_image,
      copied_from: product.source,
      item_sku: product.product_supplier_code,
    },
  };
}

interface UsePoDSearchParams {
  initialQuery?: string;
  pageSize?: number;
}

export function usePoDSearch({
  initialQuery = '',
  pageSize = 40,
}: UsePoDSearchParams = {}) {
  const [products, setProducts] = useState<SearchProduct[]>([]);
  const [query, setQueryState] = useState(initialQuery);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [appliedQuery, setAppliedQuery] = useState(initialQuery);
  const [appliedPrices, setAppliedPrices] = useState({ min: '', max: '' });
  const [offset, setOffset] = useState(0);
  const [retryTick, setRetryTick] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLastPage, setIsLastPage] = useState(false);
  // Refs mirror isLoading/error so showMore (called from an IntersectionObserver
  // that may hold a stale closure) always sees the current fetch state.
  const isLoadingRef = useRef(false);
  const hasErrorRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    isLoadingRef.current = true;
    hasErrorRef.current = false;
    setIsLoading(true);
    setError(null);

    fetchPodProducts({
      keyword: appliedQuery || undefined,
      limit: pageSize,
      offset,
      minPrice: appliedPrices.min || undefined,
      maxPrice: appliedPrices.max || undefined,
    })
      .then((batch) => {
        if (cancelled) return;
        const mapped = batch.map(mapToSearchProduct);
        setProducts((prev) => (0 === offset ? mapped : [...prev, ...mapped]));
        setIsLastPage(batch.length < pageSize);
        isLoadingRef.current = false;
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        hasErrorRef.current = true;
        setError(err instanceof Error ? err.message : 'Unable to load products');
        isLoadingRef.current = false;
        setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [appliedQuery, offset, pageSize, retryTick, appliedPrices]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedQuery((prev) => (prev === query ? prev : query));
      setOffset(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedPrices((prev) => (
        prev.min === minPrice && prev.max === maxPrice ? prev : { min: minPrice, max: maxPrice }
      ));
      setOffset(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [minPrice, maxPrice]);

  const showMore = useCallback(() => {
    // Guard: the grid's sentinel fires on mount and mid-fetch; advancing the
    // offset then would cancel the in-flight page and drop it entirely.
    if (isLoadingRef.current) return;
    if (hasErrorRef.current) {
      // Retry the failed page instead of skipping past it.
      setRetryTick((tick) => tick + 1);
      return;
    }
    setOffset((prev) => prev + pageSize);
  }, [pageSize]);

  // Offset resets in the debounce effect above, when the query is applied.
  const setQuery = setQueryState;

  const clearFilters = useCallback(() => {
    // Skip the debounce: clearing should refetch immediately.
    setMinPrice('');
    setMaxPrice('');
    setAppliedPrices({ min: '', max: '' });
    setOffset(0);
  }, []);

  return {
    products,
    query,
    setQuery,
    minPrice,
    maxPrice,
    setMinPrice,
    setMaxPrice,
    clearFilters,
    isLoading,
    error,
    isLastPage,
    showMore,
  };
}
